import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { EmployeeResource, EmployeeRoleResource } from "@/types/resources";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import FormField from "@/components/Forms/FormField";

interface FormDataT {
  name: string;
  roles: number[];
}

interface Props {
  onClose: () => void;
  editableEmployee?: EmployeeResource;
}

const EmployeeForm: FC<Props> = ({ onClose, editableEmployee }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { data: roleOptions } = useQuery(
    ["employee_roles"],
    () => api.get<never, EmployeeRoleResource[]>("/v1/employees/roles"),
    {
      select: (data) =>
        data.map((role) => ({ label: role.title, value: role.id })),
      staleTime: 120 * 1000,
    }
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableEmployee?.name ?? "",
      roles: editableEmployee?.roles?.map((r) => r.id) ?? [],
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await (editableEmployee
          ? api.put<typeof data, EmployeeResource>(
              `/v1/employees/${editableEmployee.id}`,
              data
            )
          : api.post<typeof data, EmployeeResource>(`/v1/employees`, data));

        onClose();

        queryClient.invalidateQueries(["employees"], {
          exact: false,
        });

        alerts.success(
          editableEmployee
            ? "Employee successfully updated"
            : "New employee successfully added",
          data.name
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        onClose={onClose}
        heading={editableEmployee ? "Edit employee" : "Add new"}
      >
        <SidePanelBody onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="name"
                control={control}
                label="Name"
              />
              <FormField<FormDataT, number>
                control={control}
                label="Roles"
                placeholder="Add role"
                name="roles"
                type="multiselect"
                options={roleOptions ?? []}
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {editableEmployee ? "Save" : "Add"}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default EmployeeForm;
